import React from 'react';
import styled from 'styled-components';
import Color from 'color';
import { useHistory } from 'react-router-dom';
import { Button, Divider, token, Tooltip } from '@bynder/design-system';
import { useActiveGuide, usePrefetchPage } from '../../queries/guides';
import { ROUTES } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';
import { usePageStore } from '../../stores/pageStore';
import { IconArrowBack, IconArrowForward } from '@bynder/icons';
import { useInspectorStore } from '../../stores/inspectorStore';
import { InspectorSetting } from '../../constants/guides';

const BottomNavigation: React.FC<{ activeGuideId: string }> = ({
  activeGuideId,
}) => {
  const history = useHistory();
  const activePageId = useGuideStore(s => s.activePageId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const lastSectionBackgroundColor = usePageStore(
    s => s.lastSectionBackgroundColor,
  );
  const { data: activeGuide } = useActiveGuide();
  const color = Color(lastSectionBackgroundColor);

  const iconColor = lastSectionBackgroundColor && color.isDark() && token.white;

  const [nextPage, setNextPage] = React.useState<{
    title: string | null;
    id: string | null;
  }>({
    title: null,
    id: null,
  });
  const [prevPage, setPrevPage] = React.useState<{
    title: string | null;
    id: string | null;
  }>({ title: null, id: null });

  usePrefetchPage(nextPage?.id);
  usePrefetchPage(prevPage?.id);

  React.useEffect(() => {
    const getPreviousAndNextId = (
      col: { id: string; title: string }[],
      currentId: string,
    ) => {
      if (col) {
        const currentIndex = col.findIndex(el => el.id === currentId);
        let previousIndex;
        let nextIndex;

        if (currentIndex === col.length - 1) {
          previousIndex = currentIndex - 1;
          nextIndex = -1;
        } else {
          previousIndex = currentIndex - 1;
          nextIndex = currentIndex + 1;
        }

        const nextEl = nextIndex !== -1 ? col[nextIndex] : null;
        const prevEl = previousIndex !== -1 ? col[previousIndex] : null;

        return { previous: prevEl, next: nextEl };
      }
    };
    const guidePagesIndex =
      activeGuide && activeGuide?.chapters.flatMap(chapt => chapt.pages);

    const nextAndPrevPages = getPreviousAndNextId(
      guidePagesIndex!,
      activePageId!,
    );
    const emptyState = {
      title: null,
      id: null,
    };

    setNextPage(
      nextAndPrevPages?.next
        ? {
            title: nextAndPrevPages.next.title,
            id: nextAndPrevPages.next.id,
          }
        : emptyState,
    );
    setPrevPage(
      nextAndPrevPages?.previous
        ? {
            title: nextAndPrevPages.previous.title,
            id: nextAndPrevPages.previous.id,
          }
        : emptyState,
    );
  }, [activeGuide, activeGuide?.chapters, activePageId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClickNext = () => {
    if (nextPage?.id) {
      setActiveInspectorSetting(InspectorSetting.PAGE_VIEW);
      history.push({
        pathname: ROUTES.GUIDE_PAGE(activeGuideId, nextPage.id),
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleClickPrev = () => {
    if (prevPage?.id) {
      setActiveInspectorSetting(InspectorSetting.PAGE_VIEW);
      history.push({
        pathname: ROUTES.GUIDE_PAGE(activeGuideId, prevPage.id),
      });
    }
  };

  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.shiftKey && event.code === 'ArrowLeft') {
        handleClickPrev();
      } else if (event.shiftKey && event.code === 'ArrowRight') {
        handleClickNext();
      }
    };

    if (!isEditMode) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleClickNext, handleClickPrev, isEditMode]);

  if (!prevPage?.id && !nextPage?.id) return null;

  return (
    <div>
      <StyledButtonContainer $backgroundColor={lastSectionBackgroundColor}>
        {prevPage?.id && (
          <Tooltip content={prevPage.title} timing="delay">
            <Button
              aria-label="previous"
              icon={<IconArrowBack style={{ color: iconColor || '' }} />}
              variant="clean"
              onClick={handleClickPrev}
            />
          </Tooltip>
        )}

        <StyledDivider
          direction="vertical"
          $backgroundColor={iconColor || ''}
        />

        {nextPage?.id && (
          <Tooltip content={nextPage.title} timing="delay">
            <Button
              aria-label="next"
              icon={<IconArrowForward style={{ color: iconColor || '' }} />}
              variant="clean"
              onClick={handleClickNext}
            />
          </Tooltip>
        )}
      </StyledButtonContainer>
    </div>
  );
};

export default BottomNavigation;

const StyledDivider = styled(Divider)<{ $backgroundColor: string }>`
  height: 50px;
  background-color: ${props => props.$backgroundColor};
`;

const StyledButtonContainer = styled.div<{ $backgroundColor: string }>(
  ({ theme, $backgroundColor }) => {
    const { getValueOrDefault } = theme;

    return `
  display: flex;
  padding-top: ${token.spacing5};
  padding-bottom: ${token.spacing5};
  background-color: ${$backgroundColor};
  height: 100%;
  border-top: 1px solid
    ${getValueOrDefault('navigationBar.borderColor.hexCode')};
  justify-content: center;
  align-items: center;
`;
  },
);
